import React, { useState, useEffect } from 'react';
import { Share2, X, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareFloatingButton — sticky bottom-right share widget.
 * Lets visitors share the current page via WhatsApp, Facebook, X, LinkedIn, Telegram or copy link.
 * Helps referral traffic which lifts conversions on finance sites by 10-15%.
 */
const PLATFORMS = [
  { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', buildUrl: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t}\n${u}`)}`,
    svg: 'M17.5 14.4c-.3-.1-1.7-.8-2-1s-.5-.1-.7.2-.8 1-1 1.2-.4.2-.7 0c-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5s.1-.4 0-.5l-1-2.3C8.7 6.4 8.5 6.4 8.3 6.4h-.6c-.2 0-.5.1-.8.4s-1.1 1.1-1.1 2.6 1.1 3 1.3 3.2c.2.3 2.2 3.3 5.3 4.6 1.9.8 2.6.9 3.5.8.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3A10 10 0 0 0 22 12 10 10 0 0 0 12 2z' },
  { key: 'facebook', label: 'Facebook', color: '#1877F2', buildUrl: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
    svg: 'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z' },
  { key: 'twitter', label: 'X (Twitter)', color: '#000000', buildUrl: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
    svg: 'M18.244 2H21l-6.59 7.53L22 22h-6.83l-4.7-6.18L4.84 22H2l7.07-8.08L2 2h6.96l4.25 5.65L18.24 2zm-2.4 18h1.9L8.27 4H6.26l9.58 16z' },
  { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2', buildUrl: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`,
    svg: 'M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18V9.5H5.67V18h2.67zm-1.34-10a1.55 1.55 0 1 0 0-3.09 1.55 1.55 0 0 0 0 3.09zM18 18v-4.65c0-2.49-1.34-3.65-3.13-3.65a2.7 2.7 0 0 0-2.45 1.34V9.5h-2.67V18h2.67v-4.71c0-1.25.24-2.46 1.79-2.46 1.53 0 1.55 1.43 1.55 2.54V18H18z' },
  { key: 'telegram', label: 'Telegram', color: '#26A5E4', buildUrl: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
    svg: 'M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.24 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z' },
];

const ShareFloatingButton = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('Arthik — India\u2019s Trusted Finance Marketplace');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setUrl(window.location.href);
    if (document.title) setTitle(document.title);
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy. Long-press the link.');
    }
  };

  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url, text: title });
      } catch { /* user cancelled */ }
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => (open ? setOpen(false) : handleNative())}
        aria-label="Share this page"
        data-testid="share-fab"
        style={{
          position: 'fixed',
          bottom: 'calc(env(safe-area-inset-bottom, 0) + 156px)',
          right: 20,
          zIndex: 96,
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: 0,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 8px 22px rgba(15,23,42,0.45)',
          transition: 'transform .15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {open ? <X size={20} /> : <Share2 size={20} />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="false"
          data-testid="share-menu"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0) + 210px)',
            right: 20,
            zIndex: 96,
            background: 'white',
            borderRadius: 14,
            padding: '0.85rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25), 0 0 0 1px #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: 240,
            animation: 'share-in .15s ease',
          }}
        >
          <div style={{ color: '#0F172A', fontWeight: 800, fontSize: '0.82rem', padding: '0 0.25rem 0.25rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Share2 size={14} /> Share Arthik
          </div>
          {PLATFORMS.map((p) => (
            <a
              key={p.key}
              href={p.buildUrl(url, title)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-${p.key}`}
              onClick={() => setOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 8,
                background: '#F8FAFC', color: '#0F172A',
                fontSize: '0.85rem', fontWeight: 600,
                textDecoration: 'none', transition: 'all .15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = p.color; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = '#0F172A'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d={p.svg} /></svg>
              {p.label}
            </a>
          ))}
          <button
            onClick={copyLink}
            data-testid="share-copy"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 8,
              background: '#F8FAFC', color: '#0F172A',
              fontSize: '0.85rem', fontWeight: 600,
              border: 0, cursor: 'pointer', textAlign: 'left',
            }}
          >
            {copied ? <Check size={14} style={{ color: '#10B981' }} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      )}
      <style>{`@keyframes share-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </>
  );
};

export default ShareFloatingButton;
